import * as React from 'react';
import { AccentDialog } from '../../dialogs/AccentDialog';
import { AccentButton } from '../AccentButton';
import { AccentSpinner } from '../AccentSpinner';
import { AccentPromptChat } from '../ai/AccentPromptChat';
import { AccentPromptHistory } from '../ai/AccentPromptHistory';
import { AccentTemplateTextAreaUnbound } from '../unbound/AccentTemplateTextAreaUnbound';
import { AccentTextUnbound } from '../unbound/AccentTextUnBound';
import { accentUtils, showDialog, t } from '../../services/HelperService';
import { queryNoTracking, throwIfFrameworkException } from '../../services/DataService';
import { createCustomization, saveCustomizationDraft, testCustomizationOutputScript } from '../customizations/customizationApi';
import { sendAssistantChatTurn } from '../ai/assistantChatApi';
import { promptAndReportAssistantBug } from '../ai/reportAssistantBug';
import { SaveCustomizationDlg } from '../customizations/SaveCustomizationDlg';
import { testWorkflowSetVariable } from './workflowApi';
import { AiArtifactVersionChip } from '../ai/AiArtifactVersionChip';
import {
  extractHandlebarsTokens,
  collectCatalogTokens,
  isHandlebarsToken,
  isContextPathToken,
  useAiChatAssistant,
} from '../ai/useAiChatAssistant';
import { mapFixedPicklists, mapCatalogGroup, toODataStringLiteral, toSqlStringLiteral, toJavascriptStringLiteral } from './templates/fixedPicklistLiteralCatalog';
import { PRODUCTS_TEMPLATE_GROUP, toPromptProductReference } from './templates/productCatalog';
import { normalizeJavascriptCatalog } from './AccentWorkflowJavascriptEditor';
import { isServerOwnedTemplateToken } from './ReportDateRangePicker';
import {
  QUERY_SHAPING_DEFAULT_PROFILE,
  resolveQueryShapingProfile,
  setVariableFromQueryBindingPath,
} from './queryShapingSessionProfiles';
import { DefaultQueryShapingPreview } from './DefaultQueryShapingPreview';
import { ChangeReviewChecklistPanel, parseChecklistGroups, serializeChecklistGroups } from './ChangeReviewChecklistPanel';
import { IntakeQuestionsCard, parseIntakeQuestions, INTAKE_NONE_OPTION_ID } from './IntakeQuestionsCard';

const q_TestQuery = queryNoTracking('InsyteWebApi_TestQuery', 0, true);

const STATUS_IDLE = 'idle';
const STATUS_GENERATING = 'generating';
const STATUS_TESTING = 'testing';

const safeString = (v) => (v ?? '').toString();
const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;
const isWorkflowEntityToken = (token) => /^\{\{\s*workflowEntity\./.test(safeString(token));

/** Extracts the result rows from an executed query result, mirroring the server logic. */
const extractResultRows = (result) => {
  try {
    let value = result;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return [];
      value = JSON.parse(trimmed);
    }
    if (value == null) return [];
    if (Array.isArray(value)) return value;
    if (Array.isArray(value.value)) return value.value;
    if (typeof value === 'object') return [value];
    return [];
  } catch {
    return [];
  }
};

// Bare localization keys (e.g. the AI usage limit) are translated; arbitrary error prose
// is left alone so i18n lookup quirks cannot mangle it.
const LOCALIZATION_KEY_REGEX = /^application_strings(\.[\w-]+)+$/;

const friendlyError = (raw) => {
  const s = safeString(raw).trim();
  if (!s) return '';
  try {
    const jsonStart = s.indexOf('{');
    if (jsonStart >= 0) {
      const parsed = JSON.parse(s.substring(jsonStart));
      const title = safeString(parsed?.title).trim();
      if (title) return title;
    }
  } catch { /* not JSON */ }
  return LOCALIZATION_KEY_REGEX.test(s) ? t(s) : s;
};

/**
 * Version history for several named artifacts (each query plus each output), so chat
 * messages can carry restorable per-artifact version chips.
 */
const useArtifactVersions = () => {
  const ref = React.useRef({});

  const pushVersion = React.useCallback((key, content, label, extra) => {
    const text = safeString(content);
    if (!text) return null;
    const list = ref.current[key] ?? [];
    const latest = list[list.length - 1];
    if (latest && latest.content === text) {
      // Same content, fresher execution data (e.g. an output re-run on new rows):
      // refresh the entry's extras so restoring it re-renders the latest result.
      if (extra && Object.values(extra).some((v) => v != null && v !== '')) {
        const refreshed = { ...latest, ...extra };
        ref.current = { ...ref.current, [key]: [...list.slice(0, -1), refreshed] };
        return refreshed;
      }
      return latest;
    }
    const entry = {
      id: makeId(),
      key,
      seq: (latest?.seq ?? 0) + 1,
      content: text,
      label: safeString(label),
      ...(extra ?? {}),
    };
    ref.current = { ...ref.current, [key]: [...list, entry] };
    return entry;
  }, []);

  const getVersion = React.useCallback((id) => {
    for (const list of Object.values(ref.current)) {
      const found = list.find((v) => v.id === id);
      if (found) return found;
    }
    return null;
  }, []);

  const getVersionList = React.useCallback((key) => ref.current[key] ?? [], []);

  return { pushVersion, getVersion, getVersionList };
};

const sectionStyle = { border: '1px solid #e5e5e5', borderRadius: 6, padding: 10, display: 'grid', gap: 8 };
const statusStyle = {
  padding: '8px 12px',
  borderRadius: 6,
  fontSize: 12,
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const TestOutcomeBadge = ({ ok, error }) => {
  if (ok == null) return null;
  if (ok) return <span style={{ color: '#2e7d32', fontSize: 11, fontWeight: 700 }}>{t('Test passed')}</span>;
  return (
    <span style={{ color: '#b71c1c', fontSize: 11, fontWeight: 700, wordBreak: 'break-word' }}>
      {t('Test failed')}{error ? `: ${friendlyError(error)}` : ''}
    </span>
  );
};

/**
 * Combined AI assistant for N named queries plus N JavaScript output shapes. One
 * conversation drives all artifacts: the server executes each query, grounds the outputs
 * against the real result rows, and returns typed per-artifact updates and test outcomes.
 * With no queries it runs in outputs-only mode: no query panels, and the session authors
 * output expressions against the host context alone.
 *
 * Configuration is profile-driven: sessionProfile selects both the server-side profile
 * plugin (prompt grounding and shape testing — see QueryShapingSessionProfiles, C#) and the
 * client-side behavior defaults (shape kind, add-outputs/add-queries, labels — see
 * queryShapingSessionProfiles). Hosts pass sessionProfile plus DATA props; the behavior
 * props below exist only as overrides for genuinely host-specific needs (e.g. per-trigger
 * result bindings).
 *
 * existingQueries entries are { name, query, resultBinding?, resultShape? }. When the
 * profile allows adding queries (setVariableFromQuery), bindings are derived from the
 * query name as queries.<name>; otherwise each entry's declared binding (or the profile
 * default) is used.
 */
export const AccentQueryShapingAssistantDlg = ({
  onClose,
  hostLabel,
  sessionProfile = QUERY_SHAPING_DEFAULT_PROFILE,
  existingQueries,
  existingOutputs,
  apiVersion = 'v2',
  templateCatalog,
  javascriptTemplateCatalog,
  entityFieldCatalog,
  initialVariablesJson = '{}',
  initialTemplateValues,
  initialSessionOptions,
  runtimeNotes,
  shapeKind: shapeKindProp,
  allowAddOutputs: allowAddOutputsProp,
  allowAddQueries: allowAddQueriesProp,
  outputsLabel: outputsLabelProp,
  outputsHint: outputsHintProp,
  // Product change-review sessions (profile "productChangeReview"): the computed diff
  // entries of the edit under review (serialized DiffViewer entries), the edited
  // product's id (the server loads the saved definition itself — the client never
  // uploads it), and optionally the user's intent statement.
  changeDiffJson,
  intentStatement,
  productID,
  // Product explainer sessions (profile "productExplainer"): the configuration under
  // test. A FUNCTION is snapshotted on every send, so a resumable chat always reports
  // the host's current state; a string is sent as-is.
  explainContextJson,
  // HOST PREVIEW SLOT: a React node (or array of { title, content } tabs) rendered in
  // the left pane INSTEAD of the default Preview/Artifacts tabs — hosts show their own
  // screens (the Changes diff, price details) beside the chat. When absent and the
  // profile is chat-only (no queries, no outputs), the left pane is hidden entirely.
  previewContent,
  // RESUMABLE SESSION: the session object a previous open returned in its onClose
  // payload ({ diagnosticSessionID, messages, templateValues, sessionOptions }).
  // Supplying it resumes the same conversation — same diagnostic id (server capture,
  // flow log and Send Chat as Bug stay correlated), same transcript, same options.
  // Every onClose payload carries the current session so hosts can hold it.
  initialSession,
  // Customization persistence: when true a Save button serializes the session's
  // artifacts as a Customization draft (customization = { id, name } binds the
  // session to an existing record; otherwise the first save prompts for a name and
  // creates one). onCustomizationSaved fires after every successful save.
  allowSaveAsCustomization = false,
  customization = null,
  onCustomizationSaved,
  // Customizations-page editing mode: the footer is just Save (saves the draft and
  // CLOSES the dialog) + the dialog's own Cancel. The workflow-host OK button
  // ("Use query and outputs") is meaningless here — there is no host editor
  // consuming the result — so it is not rendered.
  customizationEditor = false,
}) => {
  // Behavior defaults come from the client profile registry; an explicitly passed prop
  // (non-nullish) always wins so hosts can override one aspect without forking a profile.
  const profile = resolveQueryShapingProfile(safeString(sessionProfile));
  const shapeKind = shapeKindProp ?? profile.shapeKind;
  // Query-only profiles (hasOutputs: false) author ONLY the query: the outputs panel is
  // hidden, no output artifacts travel to the server, and the AI never proposes any.
  const hasOutputs = profile.hasOutputs !== false;
  const allowAddOutputs = hasOutputs && (allowAddOutputsProp ?? profile.allowAddOutputs);
  const allowAddQueries = allowAddQueriesProp ?? profile.allowAddQueries ?? false;
  const outputsLabel = outputsLabelProp ?? (profile.outputsLabel ? t(profile.outputsLabel) : '');
  const outputsHint = outputsHintProp ?? (profile.outputsHint ? t(profile.outputsHint) : '');

  const [queries, setQueries] = React.useState(() => (Array.isArray(existingQueries) ? existingQueries : [])
    .filter((q) => q)
    .map((q) => ({
      id: makeId(),
      name: safeString(q.name).trim() || 'query',
      query: safeString(q.query),
      resultBinding: safeString(q.resultBinding ?? profile.resultBinding),
      resultShape: q.resultShape ?? profile.resultShape,
      // Data export sessions: the mapped column whose distinct values fan this sheet
      // out into one workbook tab per value at generate time ('' = single tab).
      splitByColumn: safeString(q.splitByColumn).trim(),
      editorKey: 0,
      lastResult: null,
    })));
  const [outputs, setOutputs] = React.useState(() => (Array.isArray(existingOutputs) ? existingOutputs : [])
    .map((o) => ({
      id: makeId(),
      name: safeString(o?.name).trim(),
      script: safeString(o?.script),
      editorKey: 0,
      testOk: null,
      error: '',
      valueJson: '',
    })));
  const [templateValues, setTemplateValues] = React.useState(() => ({
    ...(initialTemplateValues ?? {}),
    // A resumed session's collected values win: they include test values the user
    // entered during the earlier open.
    ...(initialSession?.templateValues ?? {}),
  }));
  // Bumped when a suggestion chip fills a template value, so the (uncontrolled) template
  // inputs remount and show the applied value without losing focus during normal typing.
  const [templateEpoch, setTemplateEpoch] = React.useState(0);
  const [lastPromptTokens, setLastPromptTokens] = React.useState([]);
  const [status, setStatus] = React.useState(STATUS_IDLE);
  // Pipeline usage of the most recent turn (AI model calls / data API calls), shown as a
  // tiny hover hint so the cost of a turn is visible without cluttering the chat.
  const [lastTurnUsage, setLastTurnUsage] = React.useState(null);
  const sessionUsageRef = React.useRef({ ai: 0, data: 0 });
  // Per-session user choices interpreted by the profile plugin (e.g. the ad-hoc report
  // third-party-libraries consent). Sent on every chat request as sessionOptions: the
  // server profile turns them into prompt rules and artifact validators.
  const [sessionOptions, setSessionOptions] = React.useState({
    allowExternalLibraries: false,
    ...(initialSessionOptions ?? {}),
    ...(initialSession?.sessionOptions ?? {}),
  });
  // The change review's CHECKLIST artifact (profile.hasChecklist), grouped by logical
  // change: the ORACLE owns the groups and item wording (server-merged by item id),
  // the USER owns each item's state, ticked in the checklist tab. Round-trips on
  // every chat request and rides the session object across close/reopen.
  const [checklistGroups, setChecklistGroups] = React.useState(
    () => (profile.hasChecklist ? (parseChecklistGroups(initialSession?.checklistJson) ?? []) : []),
  );
  // The intent collected from the user's intake-question answers (one line per
  // answered question, accumulating across rounds). Sent as (part of) the request's
  // intentStatement so the oracle reviews against it; rides the session for resume.
  const [collectedIntent, setCollectedIntent] = React.useState(
    () => safeString(initialSession?.collectedIntent),
  );
  // The main panel is tabbed: 'artifacts' (query/output editors) and 'preview' (the
  // profile-owned preview component). Profiles pick where users land.
  const [activeTab, setActiveTab] = React.useState(profile.defaultTab === 'preview' ? 'preview' : 'artifacts');
  // Whether the most recent chat turn's automated review reported defects. Restoring a
  // version does not re-run the review, so the restore handler uses this to warn that
  // the last verdict has not been re-checked against the restored artifacts. The warn
  // fires ONCE per review (not per click): a run of restore clicks must not wallpaper
  // the chat with the same note.
  const lastReviewFailedRef = React.useRef(false);
  const reviewNoteShownRef = React.useRef(false);
  // Server context warnings repeat identically every turn until fixed; only announce
  // them when they change so they inform without flooding the chat.
  const lastContextWarningsRef = React.useRef('');

  // Client half of the profile's context contract: warn developers the moment a host
  // stops passing an expected prop, instead of the AI silently degrading. (The server
  // validates the same contract per turn and returns user-visible ContextWarnings.)
  React.useEffect(() => {
    const provided = {
      templateCatalog: Array.isArray(templateCatalog) && templateCatalog.length > 0,
      javascriptTemplateCatalog: Array.isArray(javascriptTemplateCatalog) && javascriptTemplateCatalog.length > 0,
      runtimeNotes: Array.isArray(runtimeNotes) && runtimeNotes.length > 0,
      changeDiffJson: !!safeString(changeDiffJson).trim(),
      intentStatement: !!safeString(intentStatement).trim(),
      productID: productID !== undefined && productID !== null,
      explainContextJson: typeof explainContextJson === 'function' || !!safeString(explainContextJson).trim(),
    };
    const missing = (profile.expectedContext ?? []).filter((name) => !provided[name]);
    if (missing.length > 0) {
      // eslint-disable-next-line no-console
      console.warn(
        `AccentQueryShapingAssistantDlg (${profile.key}): host did not provide expected context: ${missing.join(', ')}. ` +
        'The AI session runs degraded; fix the host editor to pass these props.');
    }
    // Contract check belongs to dialog open, not to prop churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    chatMessages,
    setChatMessages,
    pushMsg,
    buildServerMessages,
  } = useAiChatAssistant({});
  const { pushVersion, getVersion, getVersionList } = useArtifactVersions();

  // Resume a previous open's transcript: the host held the session object across a
  // close/reopen, so the conversation continues where it left off.
  React.useEffect(() => {
    if (Array.isArray(initialSession?.messages) && initialSession.messages.length > 0) {
      setChatMessages(initialSession.messages);
    }
    // Seeding belongs to dialog open only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One id per SESSION: every chat turn sends it so the server can correlate its
  // diagnostic capture with this session for "Send Chat as Bug". A resumed session
  // reuses its id, so the server-side capture, flow log and cached definitions stay
  // correlated across close/reopen.
  const diagnosticSessionIdRef = React.useRef(safeString(initialSession?.diagnosticSessionID).trim() || `qs_${makeId()}`);

  const busy = status !== STATUS_IDLE;
  const hasQueries = queries.length > 0;

  /**
   * The ctx path a query's rows bind at. Add-queries profiles derive it from the query
   * name (queries.<name>, matching the executor); fixed-query hosts declare it per entry.
   */
  const bindingFor = React.useCallback((q) => (
    allowAddQueries ? setVariableFromQueryBindingPath(q.name) : safeString(q.resultBinding).trim()
  ), [allowAddQueries]);

  // Query snippet literals follow the profile's dialect. SQL additionally quotes the
  // product ProductID tokens: their bare-guid form is an OData guid literal, which is
  // invalid inside SQL text.
  const queryCatalog = React.useMemo(() => {
    const base = Array.isArray(templateCatalog) ? templateCatalog : [];
    if (profile.queryLanguage === 'sql') {
      return mapCatalogGroup(mapFixedPicklists(base, toSqlStringLiteral), PRODUCTS_TEMPLATE_GROUP, toSqlStringLiteral);
    }
    return mapFixedPicklists(base, toODataStringLiteral);
  }, [profile.queryLanguage, templateCatalog]);
  const jsCatalogBase = React.useMemo(() => (
    Array.isArray(javascriptTemplateCatalog) && javascriptTemplateCatalog.length > 0
      ? javascriptTemplateCatalog
      : (Array.isArray(templateCatalog) ? templateCatalog : [])
  ), [javascriptTemplateCatalog, templateCatalog]);
  // Product group items carry raw ProductID guids; each editor maps its literal form:
  // the OData editor keeps the bare guid (guid literals are unquoted), the JS editor
  // quotes it, and the prompt editor inserts "Name (ProductID guid)".
  const jsCatalog = React.useMemo(
    () => mapCatalogGroup(
      normalizeJavascriptCatalog(mapFixedPicklists(jsCatalogBase, toJavascriptStringLiteral), 'ctx'),
      PRODUCTS_TEMPLATE_GROUP,
      toJavascriptStringLiteral
    ),
    [jsCatalogBase]
  );
  const promptTemplateCatalog = React.useMemo(() => {
    const catalog = mapCatalogGroup(jsCatalog, PRODUCTS_TEMPLATE_GROUP, toPromptProductReference);
    return entityFieldCatalog ? [...catalog, entityFieldCatalog] : catalog;
  }, [entityFieldCatalog, jsCatalog]);
  const queryAllowedTemplates = React.useMemo(() => collectCatalogTokens(queryCatalog), [queryCatalog]);
  // Tokens MUST be collected from the RAW catalog: normalizeJavascriptCatalog rewrites
  // {{vars.x}} tokens into ctx.vars.x snippet form, which the handlebars token collector
  // then drops — leaving the server's allowed-template list AND its derived "Known ctx
  // fields" prompt section empty. (Shipped once; the server's context validator now
  // flags this pipeline break as a warning too.) JS catalogs may carry either handlebars
  // tokens or ready-made ctx.* paths (custom validation uses the latter) — accept both.
  const shapeAllowedTemplates = React.useMemo(
    () => collectCatalogTokens(jsCatalogBase, (token) => isHandlebarsToken(token) || isContextPathToken(token)),
    [jsCatalogBase]
  );

  const usedTemplates = React.useMemo(() => {
    const tokens = [];
    for (const q of queries) {
      tokens.push(...extractHandlebarsTokens(q.query));
      const missing = q.lastResult?.MissingTemplates ?? q.lastResult?.missingTemplates ?? [];
      if (Array.isArray(missing)) tokens.push(...missing);
    }
    if (Array.isArray(lastPromptTokens)) tokens.push(...lastPromptTokens);
    // Server-owned tokens (current user) resolve on the server every run — an input
    // here would suggest the value is the user's to choose (it is not).
    return Array.from(new Set(tokens)).filter((token) => !!token && !isServerOwnedTemplateToken(token));
  }, [lastPromptTokens, queries]);

  // Hosts without a workflow entity (timer triggers run before any workflow entity
  // exists) carry no workflowEntity.* tokens in their catalog. A query holding one
  // would still test fine here — the token becomes a fillable test value — yet resolve
  // to BLANK at the real runtime, so call it out the moment such a token shows up.
  const warnedWorkflowEntityTokensRef = React.useRef(new Set());
  React.useEffect(() => {
    if (queryAllowedTemplates.some(isWorkflowEntityToken)) return;
    const offending = usedTemplates.filter((token) => isWorkflowEntityToken(token)
      && !warnedWorkflowEntityTokensRef.current.has(token));
    if (offending.length === 0) return;
    for (const token of offending) warnedWorkflowEntityTokensRef.current.add(token);
    pushMsg('error',
      `${t('The workflow entity is not available in this context, so these tokens will resolve to blank when the workflow runs')}: ${offending.join(', ')}. ${t('Remove them and select the rows another way (e.g. by ID or status).')}`);
  }, [pushMsg, queryAllowedTemplates, usedTemplates]);

  /**
   * First-class locals for output tests: every query's latest result rows bound at its
   * declared ctx path (plus a ".count" sibling for ".rows" bindings). Locals are the
   * same channel the engine and the server-side session use — the shared JS harness
   * overlays them onto ctx LAST, so fresh rows always beat any stale template value or
   * variable snapshot with the same name. Never inject result rows via template values.
   */
  const buildOutputTestLocals = React.useCallback(() => {
    const locals = {};
    for (const q of queries) {
      const binding = bindingFor(q);
      if (!binding) continue;
      const rows = extractResultRows(q.lastResult?.Result ?? q.lastResult?.result);
      locals[binding] = rows.slice(0, 100);
      if (binding.endsWith('.rows')) {
        locals[`${binding.slice(0, -'.rows'.length)}.count`] = rows.length;
      }
    }
    return locals;
  }, [bindingFor, queries]);

  const updateQuery = React.useCallback((id, patch) => {
    setQueries((prev) => prev.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  }, []);

  const updateOutput = React.useCallback((id, patch) => {
    setOutputs((prev) => prev.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  }, []);

  /** A short display label for a query artifact ("Query" while there is only one). */
  const queryLabel = React.useCallback((q) => (
    queries.length > 1 || allowAddQueries ? (q.name || t('Query')) : t('Query')
  ), [allowAddQueries, queries.length]);

  const announceQueryResult = React.useCallback((label, queryResult) => {
    if (!queryResult) return;
    const testOk = !!(queryResult.TestOk ?? queryResult.testOk);
    const errorMsg = safeString(queryResult.Error ?? queryResult.error);
    const errCode = safeString(queryResult.ErrorCode ?? queryResult.errorCode).toLowerCase();
    const rowCount = queryResult.ValueCount ?? queryResult.valueCount;

    if (testOk) {
      pushMsg('system', rowCount === 0
        ? `${label}: ${t('test passed but returned no rows — check that filter values match real data.')}`
        : `${label}: ${t('test passed.')}`);
    } else if (errCode === 'missingtemplatevalues') {
      const tokens = queryResult.MissingTemplates ?? queryResult.missingTemplates ?? [];
      pushMsg('system', `${label}: ${t('enter test values for')} ${tokens.join(', ')} ${t('below, then test again.')}`);
    } else if (errorMsg) {
      pushMsg('error', `${label}: ${t('test failed')} — ${friendlyError(errorMsg)}`);
    }
  }, [pushMsg]);

  // collectedIntentOverride: the intake submit handler updates the collected intent
  // and sends the turn in the same tick — state has not re-rendered yet, so the fresh
  // value rides as an explicit override (the SAME turn runs the review against it).
  const onChatPrompt = React.useCallback(async ({ text, history, intent, collectedIntentOverride }) => {
    try {
      setLastPromptTokens(extractHandlebarsTokens(text));

      // Record manual edits as versions so restore points stay coherent. Keep the
      // pre-turn entry per artifact: it becomes the restorable "previous" chip when
      // the AI changes that artifact this turn.
      const preTurn = {};
      for (const q of queries) {
        preTurn[`query:${q.id}`] = pushVersion(`query:${q.id}`, q.query, queryLabel(q));
      }
      for (const o of outputs) {
        // Versions carry the script's last EXECUTED value too, so restoring an output
        // re-renders the preview (e.g. the ad-hoc report HTML) without re-running anything.
        if (o.name) preTurn[`output:${o.id}`] = pushVersion(`output:${o.id}`, o.script, o.name, { valueJson: safeString(o.valueJson) });
      }

      // Superseded versions (excluding the current content) so the AI can reference
      // or restore earlier artifacts the user mentions.
      const priorContents = (key) => getVersionList(key).slice(0, -1).slice(-5).map((v) => v.content);

      setStatus(STATUS_GENERATING);
      // Shared envelope + send (see assistantChatApi.js): handled 500s throw their
      // translated message so the catch below reports them as an error turn.
      const res = await sendAssistantChatTurn({
        prompt: safeString(text),
        messages: buildServerMessages(history, text),
        queries: queries.map((q) => ({
          name: q.name,
          query: safeString(q.query),
          expectedResultShape: q.resultShape,
          resultBinding: bindingFor(q),
          priorVersions: priorContents(`query:${q.id}`),
          // Round-trip the server's "automatic repair gave up on this text" flag so the
          // next turn steers the AI toward a structurally different query. The server
          // clears it itself as soon as the query passes again.
          repairExhausted: !!(q.lastResult?.RepairExhausted ?? q.lastResult?.repairExhausted),
          splitByColumn: safeString(q.splitByColumn).trim() || undefined,
        })),
        shapes: outputs
          .filter((o) => !!o.name)
          .map((o) => ({
            name: o.name,
            script: safeString(o.script),
            kind: shapeKind,
            priorVersions: priorContents(`output:${o.id}`),
          })),
        allowNewShapes: allowAddOutputs,
        allowNewQueries: allowAddQueries,
        apiVersion,
        templateValues,
        queryAllowedTemplates,
        shapeAllowedTemplates,
        variablesJson: safeString(initialVariablesJson) || '{}',
        runtimeNotes: Array.isArray(runtimeNotes) ? runtimeNotes : [],
        hostLabel: safeString(hostLabel),
        diagnosticSessionID: diagnosticSessionIdRef.current,
        sessionProfile: safeString(sessionProfile),
        sessionOptions,
        // Explicit intent from the composer's action buttons ('ask' | 'change'); empty
        // when sent with Enter. 'ask' turns are server-enforced read-only.
        promptIntent: safeString(intent),
        // Change-review context: the diff entries plus the edited product's id (the
        // server loads the saved "before" definition itself). Undefined for other hosts.
        changeDiffJson: safeString(changeDiffJson).trim() || undefined,
        // The host's own intent statement (if any) plus the intent collected from the
        // intake-question answers — first-class so review turns judge against it.
        intentStatement: [
          safeString(intentStatement).trim(),
          safeString(collectedIntentOverride ?? collectedIntent).trim(),
        ].filter(Boolean).join('\n') || undefined,
        changeProductID: productID ?? undefined,
        // Explainer context: snapshotted per SEND when the host passes a function, so
        // a resumed chat always sees the tester's current state.
        explainContextJson: (typeof explainContextJson === 'function'
          ? safeString(explainContextJson())
          : safeString(explainContextJson)).trim() || undefined,
        // Change-review checklist round-trip: the current groups INCLUDING the user's
        // tick states — the server preserves those by item id when a re-assessment
        // revises the content. Undefined for other profiles and before the first checklist.
        checklistJson: profile.hasChecklist && checklistGroups.length > 0
          ? serializeChecklistGroups(checklistGroups)
          : undefined,
      });

      const message = safeString(res?.Message ?? res?.message);
      const versionsForMessage = [];

      // Pipeline usage counters for this turn (every AI model call and data API call the
      // server made end-to-end, including repairs, probes and oracle rounds).
      const aiCallCount = Number(res?.AiCallCount ?? res?.aiCallCount ?? 0);
      const dataApiCallCount = Number(res?.DataApiCallCount ?? res?.dataApiCallCount ?? 0);
      if (aiCallCount > 0 || dataApiCallCount > 0) {
        sessionUsageRef.current = {
          ai: sessionUsageRef.current.ai + aiCallCount,
          data: sessionUsageRef.current.data + dataApiCallCount,
        };
        setLastTurnUsage({ ai: aiCallCount, data: dataApiCallCount });
      }

      // Review outcome and server notices arrive with the artifacts; a FAILED review
      // (concrete defects) gates every success indicator below — such a turn must never
      // present plain green chips/badges even when the machine tests passed. Advisory
      // review remarks are informational and gate nothing.
      const reviewNotes = (res?.ReviewNotes ?? res?.reviewNotes ?? []).map(safeString).filter(Boolean);
      const reviewFailed = !!(res?.ReviewFailed ?? res?.reviewFailed);
      const reviewAdvisories = (res?.ReviewAdvisories ?? res?.reviewAdvisories ?? []).map(safeString).filter(Boolean);
      const reviewScoreRaw = res?.ReviewScore ?? res?.reviewScore;
      const reviewScore = typeof reviewScoreRaw === 'number' ? reviewScoreRaw : null;
      const reviewConfidence = safeString(res?.ReviewConfidence ?? res?.reviewConfidence).toLowerCase();
      const reviewSummary = safeString(res?.ReviewSummary ?? res?.reviewSummary);
      // Notices are severity-typed on the server: an honestly-disclosed limitation must
      // not render in the same red as a hard failure next to a green review score.
      const notices = (res?.Notices ?? res?.notices ?? [])
        .map((n) => ({
          text: safeString(n?.Text ?? n?.text),
          severity: safeString(n?.Severity ?? n?.severity).toLowerCase(),
        }))
        .filter((n) => n.text);
      const gatedOk = (ok) => (reviewFailed ? null : ok);
      // Remembered so a later version restore can warn that the review has not re-checked
      // the restored artifacts. A fresh verdict re-arms the once-per-review restore note.
      lastReviewFailedRef.current = reviewFailed;
      reviewNoteShownRef.current = false;

      // Artifacts the assistant REMOVED this turn (the remove-directive protocol) —
      // explicit, because results are merged by name and mere absence from the response
      // is not a removal. Version history for removed artifacts stays restorable.
      const removedQueryNames = new Set((res?.RemovedQueries ?? res?.removedQueries ?? [])
        .map((n) => safeString(n).trim().toLowerCase())
        .filter(Boolean));
      const removedOutputNames = new Set((res?.RemovedOutputs ?? res?.removedOutputs ?? [])
        .map((n) => safeString(n).trim().toLowerCase())
        .filter(Boolean));

      // Query updates, matched to session queries by name.
      const queryResults = res?.Queries ?? res?.queries ?? [];
      let nextQueries = [...queries];
      for (const queryResult of queryResults) {
        const name = safeString(queryResult?.Name ?? queryResult?.name).trim();
        let ix = nextQueries.findIndex((q) => q.name.toLowerCase() === name.toLowerCase());
        if (ix < 0) {
          // A query the AI introduced this turn (multi-query strategy). Only add-queries
          // hosts accept these; the server enforces the same capability, so an unknown
          // name on a fixed-query host is simply ignored.
          const isNew = !!(queryResult?.New ?? queryResult?.new);
          if (!isNew || !allowAddQueries || !name) continue;
          nextQueries.push({
            id: makeId(),
            name,
            query: '',
            resultBinding: '',
            resultShape: profile.resultShape,
            splitByColumn: '',
            editorKey: 0,
            lastResult: null,
          });
          ix = nextQueries.length - 1;
        }
        const entry = nextQueries[ix];

        // The split state always mirrors the server's verdict for this turn (set,
        // changed, or cleared by the assistant's split directive).
        const splitByColumn = safeString(queryResult.SplitByColumn ?? queryResult.splitByColumn).trim();

        const updated = !!(queryResult.Updated ?? queryResult.updated);
        const nextQueryText = safeString(queryResult.Query ?? queryResult.query);
        if (updated && !accentUtils.isEmpty(nextQueryText)) {
          const version = pushVersion(`query:${entry.id}`, nextQueryText, queryLabel(entry));
          if (version) {
            // Also surface the version that was current before this change, so the
            // user can restore it with one click.
            const prior = preTurn[`query:${entry.id}`];
            if (prior && prior.id !== version.id) {
              versionsForMessage.push({ ...prior, testOk: null });
            }
            versionsForMessage.push({ ...version, testOk: gatedOk(!!(queryResult.TestOk ?? queryResult.testOk)) });
          }
          nextQueries[ix] = { ...entry, query: nextQueryText, splitByColumn, editorKey: entry.editorKey + 1, lastResult: queryResult };
        } else {
          nextQueries[ix] = { ...entry, splitByColumn, lastResult: queryResult };
        }
        if (updated) announceQueryResult(queryLabel(entry), queryResult);

        // Only worth announcing on turns that actually changed something: repeating it on
        // every conversational turn buries the messages that matter (review, corrections).
        if (updated && (queryResult.SampleRowsUsedForTests ?? queryResult.sampleRowsUsedForTests)) {
          pushMsg('system', `${queryLabel(entry)}: ${t('the query returned no rows, so outputs were tested against sample rows from the entity (real field names, not your query result).')}`);
        }
      }
      if (removedQueryNames.size > 0) {
        nextQueries = nextQueries.filter((q) => !removedQueryNames.has(q.name.toLowerCase()));
      }
      setQueries(nextQueries);

      // Output updates (including brand-new outputs the AI introduced).
      const shapeResults = res?.Shapes ?? res?.shapes ?? [];
      let nextOutputs = [...outputs];
      for (const shape of shapeResults) {
        const name = safeString(shape?.Name ?? shape?.name).trim();
        if (!name) continue;
        const updated = !!(shape.Updated ?? shape.updated);
        const tested = !!(shape.Tested ?? shape.tested);
        const script = safeString(shape.Script ?? shape.script);
        const testOk = !!(shape.TestOk ?? shape.testOk);
        const error = safeString(shape.Error ?? shape.error);
        const valueJson = safeString(shape.ValueJson ?? shape.valueJson);

        let ix = nextOutputs.findIndex((o) => o.name.toLowerCase() === name.toLowerCase());
        if (ix < 0) {
          if (!updated || !allowAddOutputs) continue;
          nextOutputs.push({ id: makeId(), name, script: '', editorKey: 0, testOk: null, error: '', valueJson: '' });
          ix = nextOutputs.length - 1;
        }

        const entry = nextOutputs[ix];
        if (updated && !accentUtils.isEmpty(script)) {
          const version = pushVersion(`output:${entry.id}`, script, entry.name || name, { valueJson });
          if (version) {
            const prior = preTurn[`output:${entry.id}`];
            if (prior && prior.id !== version.id) {
              versionsForMessage.push({ ...prior, testOk: null });
            }
            versionsForMessage.push({ ...version, testOk: gatedOk(testOk) });
          }
          nextOutputs[ix] = { ...entry, script, editorKey: entry.editorKey + 1, testOk: gatedOk(testOk), error, valueJson };
        } else if (tested) {
          nextOutputs[ix] = { ...entry, testOk: entry.script ? gatedOk(testOk) : entry.testOk, error, valueJson };
        }
        // Not updated and not tested (a conversational turn): keep the entry's previous
        // state — default TestOk=false from an untested result must not look like a failure.
      }
      if (removedOutputNames.size > 0) {
        nextOutputs = nextOutputs.filter((o) => !removedOutputNames.has(safeString(o.name).trim().toLowerCase()));
      }
      setOutputs(nextOutputs);

      if (removedQueryNames.size > 0 || removedOutputNames.size > 0) {
        const removedNote = [
          ...[...removedQueryNames].map((n) => `${t('query')} '${n}'`),
          ...[...removedOutputNames].map((n) => `${t('output')} '${n}'`),
        ].join(', ');
        pushMsg('system', `${t('Removed from this session')}: ${removedNote}`);
      }

      // Review-checklist turn result: the server ships the FULL merged checklist only
      // when the turn actually changed it (ChecklistUpdated); otherwise the client
      // keeps what it holds — including the user's tick states, which never leave
      // the client except via the request round-trip above.
      if (res?.ChecklistUpdated ?? res?.checklistUpdated) {
        const mergedGroups = parseChecklistGroups(safeString(res?.ChecklistJson ?? res?.checklistJson));
        if (mergedGroups) {
          setChecklistGroups(mergedGroups);
          pushMsg('system', t('The review checklist was updated — work through the items in the Checklist tab.'));
        }
      }

      // The server's oracle round record (the brief + planner answer) travels as a hidden
      // "tool" message: never rendered, but round-tripped with the transcript so the next
      // turn's assistant can refine the previous brief instead of reconstructing intent.
      const toolRecord = safeString(res?.ToolRecord ?? res?.toolRecord);
      if (toolRecord) {
        pushMsg('tool', toolRecord);
      }

      // Test template values the find-record skill discovered in real rows; rendered as
      // one-click "use this value" chips on the assistant message.
      const suggestedTemplateValues = res?.SuggestedTemplateValues ?? res?.suggestedTemplateValues ?? null;
      const templateSuggestions = suggestedTemplateValues && typeof suggestedTemplateValues === 'object'
        ? Object.entries(suggestedTemplateValues)
            .map(([token, value]) => ({ token: safeString(token), value: safeString(value) }))
            .filter((s) => s.token && s.value)
        : [];

      // A round of intent-intake questions rides the assistant message as a checkbox
      // card (see IntakeQuestionsCard). The parsed questions persist on the message,
      // so a resumed session re-renders the card — answered rounds stay frozen via
      // the intakeAnswered/intakeSelections markers the submit handler adds.
      const intakeQuestions = parseIntakeQuestions(res?.IntakeQuestionsJson ?? res?.intakeQuestionsJson);

      pushMsg('assistant', message || t('I could not produce a response. Please try rephrasing.'), {
        versions: versionsForMessage.length > 0
          ? versionsForMessage.map((v) => ({ id: v.id, seq: v.seq, label: v.label, testOk: v.testOk }))
          : undefined,
        templateSuggestions: templateSuggestions.length > 0 ? templateSuggestions : undefined,
        intakeQuestions: intakeQuestions ?? undefined,
      });

      // Server-side notices, routed by declared severity. Real failures stay as
      // individual red bubbles; warnings/info (mostly disclosed API limitations) are
      // grouped into ONE collapsible "Limitations & notes" list — attached to the review
      // bubble below when a review ran — so a turn with several honest caveats doesn't
      // render as a wall of amber.
      const softNotices = [];
      for (const notice of notices) {
        if (notice.severity === 'error') {
          pushMsg('error', notice.text);
        } else {
          softNotices.push(notice);
        }
      }

      for (const shape of shapeResults) {
        const updated = !!(shape.Updated ?? shape.updated);
        if (!updated) continue;
        const name = safeString(shape.Name ?? shape.name);
        const testOk = !!(shape.TestOk ?? shape.testOk);
        const error = safeString(shape.Error ?? shape.error);
        if (testOk && !reviewFailed) {
          pushMsg('system', `Output '${name}' test passed.`);
        } else if (error) {
          pushMsg('error', `Output '${name}' test failed: ${friendlyError(error)}`);
        }
      }

      // Automated semantic review of the turn (runs when everything passed its tests).
      // Rendered as ONE graded 'review' message — score-colored bubble, not an error —
      // present even on clean passes so the user sees the check actually ran. Only
      // reviewFailed (unresolved defects) gates the green success indicators.
      if (reviewScore != null || reviewNotes.length > 0 || reviewAdvisories.length > 0) {
        pushMsg('review',
          reviewSummary
            || (reviewFailed
              ? t('The automated review found issues the assistant could not fix automatically.')
              : t('The automated review checked this turn\'s artifacts.')),
          {
            review: {
              score: reviewScore,
              confidence: reviewConfidence,
              failed: reviewFailed,
              defects: reviewNotes,
              advisories: reviewAdvisories,
              notices: softNotices,
            },
          });
      } else if (softNotices.length > 0) {
        // No review verdict this turn (planner failure, review call failed, ...) — the
        // grouped notes still need a home of their own.
        pushMsg('notices', t('Limitations & notes'), { notices: softNotices });
      }

      // Context validation: the session ran with missing/dropped context (see the server's
      // profile context contract). Neutral note — the artifacts still work, but answer
      // quality is degraded and someone should fix the host wiring.
      const contextWarnings = (res?.ContextWarnings ?? res?.contextWarnings ?? []).map(safeString).filter(Boolean);
      const contextWarningsKey = contextWarnings.join('|');
      if (contextWarnings.length > 0 && lastContextWarningsRef.current !== contextWarningsKey) {
        pushMsg('system', `${t('Assistant context is incomplete')}: ${contextWarnings.join(' \u2022 ')}`);
      }
      lastContextWarningsRef.current = contextWarningsKey;

      return true;
    } catch (err) {
      // Server errors may be localization keys (e.g. the AI usage limit); resolve them.
      const raw = safeString(err?.message) || safeString(err);
      pushMsg('error', raw ? t(raw) : t('AI request failed.'));
      return false;
    } finally {
      setStatus(STATUS_IDLE);
    }
  }, [allowAddOutputs, allowAddQueries, announceQueryResult, apiVersion, bindingFor, buildServerMessages, changeDiffJson, checklistGroups, collectedIntent, explainContextJson, getVersionList, hostLabel, initialVariablesJson, intentStatement, outputs, productID, profile.hasChecklist, profile.resultShape, pushMsg, pushVersion, queries, queryAllowedTemplates, queryLabel, runtimeNotes, sessionOptions, sessionProfile, shapeAllowedTemplates, shapeKind, templateValues]);

  // One intake round answered: the ticked options become the next chat turn (prose
  // the assistant reads like any answer), the card freezes on its message (the
  // markers ride the saved session, so resumed transcripts keep the record), and the
  // selections join the collected intent — passed as an override too, because this
  // SAME turn may run the review and must judge against the fresh intent.
  const submitIntakeAnswers = React.useCallback((messageId, questions, selections) => {
    const answered = (Array.isArray(questions) ? questions : []).map((q) => {
      const chosen = selections?.[q.id] ?? [];
      const chosenText = chosen.length === 0 || chosen.includes(INTAKE_NONE_OPTION_ID)
        ? t('None of the above')
        : q.options.filter((o) => chosen.includes(o.id)).map((o) => o.text).join('; ');
      return `${q.text} — ${chosenText}`;
    });
    if (answered.length === 0) return;

    setChatMessages((prev) => (Array.isArray(prev) ? prev : []).map((msg) => (
      msg.id === messageId ? { ...msg, intakeAnswered: true, intakeSelections: selections } : msg
    )));

    const nextCollectedIntent = [safeString(collectedIntent).trim(), ...answered]
      .filter(Boolean).join('\n');
    setCollectedIntent(nextCollectedIntent);

    const text = `${t('My answers:')}\n${answered.map((line) => `- ${line}`).join('\n')}`;
    pushMsg('user', text, { intent: 'change' });
    const history = [
      ...chatMessages.map((m) => ({
        role: safeString(m?.role || 'user'),
        text: safeString(m?.text),
        intent: safeString(m?.intent),
      })),
      { role: 'user', text, intent: 'change' },
    ].filter((m) => m.text.trim());
    onChatPrompt({ text, history, intent: 'change', collectedIntentOverride: nextCollectedIntent });
  }, [chatMessages, collectedIntent, onChatPrompt, pushMsg, setChatMessages]);

  const onRestoreVersion = React.useCallback((versionId) => {
    const version = getVersion(versionId);
    if (!version) return;

    // Restoring does NOT re-run the automated review, so warn once (not per click) that
    // the last verdict has not been re-checked against the restored artifacts.
    if (lastReviewFailedRef.current && !reviewNoteShownRef.current) {
      reviewNoteShownRef.current = true;
      pushMsg('system', t('Note: the automated review reported issues on this session and has not re-checked the restored version — its findings may or may not still apply.'));
    }

    if (version.key.startsWith('query:')) {
      const queryId = version.key.slice('query:'.length);
      setQueries((prev) => {
        const ix = prev.findIndex((q) => q.id === queryId);
        if (ix < 0) {
          pushMsg('system', t('The query for this version no longer exists.'));
          return prev;
        }
        const list = [...prev];
        list[ix] = { ...list[ix], query: version.content, editorKey: list[ix].editorKey + 1, lastResult: null };
        // A restored query has not RUN: the preview still shows the previous execution.
        pushMsg('system', `Restored ${version.label || t('query')} v${version.seq}. ${t('The preview still shows the last run — use Refresh to re-run with the restored query.')}`);
        // Hidden transcript record (role 'tool' round-trips to the server, never
        // rendered): without it the model's last knowledge of this artifact is its own
        // "I updated it" claim, so it keeps describing the rolled-back version.
        pushMsg('tool', `SESSION RECORD: the user RESTORED query '${version.label || queryId}' to version ${version.seq}. Anything earlier in this conversation describing a newer version of this query is now stale — the query text in the current session state is the truth. The restored query has not been run yet.`);
        return list;
      });
      return;
    }

    const outputId = version.key.startsWith('output:') ? version.key.slice('output:'.length) : '';
    setOutputs((prev) => {
      const ix = prev.findIndex((o) => o.id === outputId);
      if (ix < 0) {
        pushMsg('system', t('The output for this version no longer exists.'));
        return prev;
      }
      const restoredValueJson = safeString(version.valueJson);
      const list = [...prev];
      // Versions carry the value the script produced when it ran, so the preview
      // re-renders the restored version immediately; without one the value clears
      // and Refresh re-executes on demand.
      list[ix] = { ...list[ix], script: version.content, editorKey: list[ix].editorKey + 1, testOk: null, error: '', valueJson: restoredValueJson };
      pushMsg('system', restoredValueJson
        ? `Restored ${version.label || t('output')} v${version.seq}.`
        : `Restored ${version.label || t('output')} v${version.seq}. ${t('This version has no stored result — use Refresh to run it.')}`);
      // Hidden transcript record (role 'tool' round-trips to the server, never
      // rendered): without it the model's last knowledge of this artifact is its own
      // "I updated it" claim, so it keeps describing the rolled-back version.
      pushMsg('tool', `SESSION RECORD: the user RESTORED output '${version.label || outputId}' to version ${version.seq}. Anything earlier in this conversation describing a newer version of this output is now stale — the output's expression in the current session state is the truth.`);
      return list;
    });
  }, [getVersion, pushMsg]);

  // Fills a template value the find-record skill discovered. The token is added to the
  // visible template list and the inputs are remounted (epoch) so the value shows.
  const onApplyTemplateSuggestion = React.useCallback((token, value) => {
    setTemplateValues((prev) => ({ ...(prev ?? {}), [token]: safeString(value) }));
    setLastPromptTokens((prev) => Array.from(new Set([...(prev ?? []), token])));
    setTemplateEpoch((e) => e + 1);
    pushMsg('system', `${t('Test value set')}: ${token} = ${safeString(value)}`);
  }, [pushMsg]);

  const renderMessageExtra = React.useCallback((m) => {
    const versions = Array.isArray(m?.versions) ? m.versions : [];
    const suggestions = Array.isArray(m?.templateSuggestions) ? m.templateSuggestions : [];
    const intake = Array.isArray(m?.intakeQuestions) ? m.intakeQuestions : [];
    if (versions.length === 0 && suggestions.length === 0 && intake.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {intake.length > 0 && (
          <IntakeQuestionsCard
            questions={intake}
            answered={!!m.intakeAnswered}
            selections={m.intakeSelections}
            disabled={busy}
            onSubmit={(selections) => submitIntakeAnswers(m.id, intake, selections)}
          />
        )}
        {(versions.length > 0 || suggestions.length > 0) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {versions.map((v) => (
              <AiArtifactVersionChip
                key={v.id}
                label={`${v.label || t('Artifact')} v${v.seq}`}
                ok={v.testOk}
                title={t('Restore this version into the editor')}
                onClick={() => onRestoreVersion(v.id)}
              />
            ))}
            {suggestions.map((s) => (
              <AiArtifactVersionChip
                key={s.token}
                label={`${s.token} = ${s.value}`}
                title={t('Use this found value as the test value for the template token')}
                onClick={() => onApplyTemplateSuggestion(s.token, s.value)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }, [busy, onApplyTemplateSuggestion, onRestoreVersion, submitIntakeAnswers]);

  const onTestQueryClick = async (queryEntry, e) => {
    try {
      setStatus(STATUS_TESTING);
      pushMsg('system', `${t('Testing')} ${queryLabel(queryEntry)}...`);
      const res = throwIfFrameworkException(await q_TestQuery.getFirstOrDefault({
        query: safeString(queryEntry.query),
        expectedResultShape: queryEntry.resultShape,
        apiVersion,
        queryLanguage: profile.queryLanguage,
        templateValues,
      }));
      updateQuery(queryEntry.id, { lastResult: res });
      announceQueryResult(queryLabel(queryEntry), res);
    } catch (err) {
      const errMsg = safeString(err?.message) || safeString(err);
      updateQuery(queryEntry.id, { lastResult: { TestOk: false, Error: errMsg } });
      pushMsg('error', `${queryLabel(queryEntry)}: ${t('test error')} — ${errMsg}`);
    } finally {
      setStatus(STATUS_IDLE);
      e?.promise?.();
    }
  };

  // Customization sessions evaluate outputs through the customization endpoints (web
  // app in-process, customization-gated); workflow sessions keep the engine-backed
  // node-test endpoints so tests stay faithful to the engine runtime.
  const runOutputScriptTest = React.useCallback(
    (args) => (profile.nodeTestScope === 'customization' ? testCustomizationOutputScript(args) : testWorkflowSetVariable(args)),
    [profile.nodeTestScope]
  );

  const onTestOutputClick = async (output, e) => {
    try {
      setStatus(STATUS_TESTING);
      const res = throwIfFrameworkException(await runOutputScriptTest({
        name: output.name || 'result',
        valueMode: 'javascript',
        valueTemplate: '',
        script: safeString(output.script),
        variablesJson: safeString(initialVariablesJson) || '{}',
        templateValues: { ...(templateValues ?? {}) },
        localsJson: JSON.stringify(buildOutputTestLocals()),
      }));
      const success = !!(res?.success ?? res?.Success);
      const error = safeString(res?.error ?? res?.Error);
      updateOutput(output.id, {
        testOk: success,
        error,
        valueJson: safeString(res?.valueJson ?? res?.ValueJson),
      });
      if (success) {
        pushMsg('system', `Output '${output.name}' test passed.`);
      } else if (error) {
        pushMsg('error', `Output '${output.name}' test failed: ${friendlyError(error)}`);
      }
    } catch (err) {
      const errMsg = safeString(err?.message) || safeString(err);
      updateOutput(output.id, { testOk: false, error: errMsg, valueJson: '' });
      pushMsg('error', `Output test error: ${errMsg}`);
    } finally {
      setStatus(STATUS_IDLE);
      e?.promise?.();
    }
  };

  /**
   * Merges preview-supplied template values (e.g. the report date-range picker's
   * computed instants) into the session's template values and remounts the inputs so
   * the Artifacts tab shows them.
   */
  const onTemplateValuesPatch = React.useCallback((patch) => {
    if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return;
    setTemplateValues((prev) => ({ ...(prev ?? {}), ...patch }));
    setTemplateEpoch((e) => e + 1);
  }, []);

  /**
   * Re-runs every query, then re-tests every output against the fresh rows — the
   * profile preview's "Refresh" action (e.g. re-render an ad-hoc report on live data).
   * Fresh results are collected locally because the output tests must bind THIS run's
   * rows, not whatever state React has committed so far.
   *
   * templateValuePatch (optional): template values to merge in and run with THIS
   * refresh — the caller's state update has not committed yet, so the run must not rely
   * on the closed-over templateValues (e.g. the report date-range picker's new range).
   */
  const onRefreshAll = React.useCallback(async (templateValuePatch) => {
    const patch = templateValuePatch && typeof templateValuePatch === 'object' && !Array.isArray(templateValuePatch)
      ? templateValuePatch
      : null;
    if (patch) onTemplateValuesPatch(patch);
    const effectiveTemplateValues = patch ? { ...(templateValues ?? {}), ...patch } : templateValues;
    try {
      setStatus(STATUS_TESTING);

      // The queries are independent, so they run in PARALLEL — a refresh costs the
      // slowest query, not the sum. Each task catches its own failure so one bad query
      // never sinks the batch; announcements happen after, in stable query order.
      const runnable = queries.filter((q) => !accentUtils.isEmpty(q.query));
      const settled = await Promise.all(runnable.map(async (q) => {
        try {
          const res = throwIfFrameworkException(await q_TestQuery.getFirstOrDefault({
            query: safeString(q.query),
            expectedResultShape: q.resultShape,
            apiVersion,
            queryLanguage: profile.queryLanguage,
            templateValues: effectiveTemplateValues,
            // Profiles whose real runtime honors the query verbatim (data export: the
            // query's own row limit is the sheet's) bound their previews here instead.
            maxPreviewRows: profile.previewMaxRows,
          }));
          return [q, res];
        } catch (err) {
          return [q, { TestOk: false, Error: safeString(err?.message) || safeString(err) }];
        }
      }));

      const freshResults = new Map();
      for (const [q, res] of settled) {
        freshResults.set(q.id, res);
        announceQueryResult(queryLabel(q), res);
      }
      setQueries((prev) => prev.map((q) => (freshResults.has(q.id) ? { ...q, lastResult: freshResults.get(q.id) } : q)));

      // Unlike the editors' 100-row test cap, refresh binds the FULL result rows: the
      // preview (e.g. a top-N report) must render exactly what the queries returned.
      const locals = {};
      for (const q of queries) {
        const binding = bindingFor(q);
        if (!binding) continue;
        const latest = freshResults.get(q.id) ?? q.lastResult;
        const rows = extractResultRows(latest?.Result ?? latest?.result);
        locals[binding] = rows;
        if (binding.endsWith('.rows')) {
          locals[`${binding.slice(0, -'.rows'.length)}.count`] = rows.length;
        }
      }

      // Outputs are independent of each other (they all bind the same fresh locals),
      // so they re-test in parallel too; per-task catches keep failures isolated.
      await Promise.all(outputs
        .filter((output) => !accentUtils.isEmpty(output.script))
        .map(async (output) => {
          try {
            const res = throwIfFrameworkException(await runOutputScriptTest({
              name: output.name || 'result',
              valueMode: 'javascript',
              valueTemplate: '',
              script: safeString(output.script),
              variablesJson: safeString(initialVariablesJson) || '{}',
              templateValues: { ...(effectiveTemplateValues ?? {}) },
              localsJson: JSON.stringify(locals),
            }));
            const success = !!(res?.success ?? res?.Success);
            const error = safeString(res?.error ?? res?.Error);
            updateOutput(output.id, { testOk: success, error, valueJson: safeString(res?.valueJson ?? res?.ValueJson) });
            if (!success && error) {
              pushMsg('error', `Output '${output.name}' refresh failed: ${friendlyError(error)}`);
            }
          } catch (err) {
            const errMsg = safeString(err?.message) || safeString(err);
            updateOutput(output.id, { testOk: false, error: errMsg, valueJson: '' });
            pushMsg('error', `Output '${output.name}' refresh failed: ${errMsg}`);
          }
        }));
    } finally {
      setStatus(STATUS_IDLE);
    }
  }, [announceQueryResult, apiVersion, bindingFor, initialVariablesJson, onTemplateValuesPatch, outputs, profile.previewMaxRows, profile.queryLanguage, pushMsg, queries, queryLabel, runOutputScriptTest, templateValues, updateOutput]);

  // One automatic artifact run when the dialog opens over EXISTING artifacts (editing
  // a saved customization): outputs seed with their scripts but no evaluated value, so
  // previews that read output values (the data grid's declared definition) would
  // otherwise render nothing until the first manual Refresh or chat turn.
  const initialRunRef = React.useRef(false);
  React.useEffect(() => {
    if (initialRunRef.current) return;
    const needsRun = outputs.some((o) => !accentUtils.isEmpty(o.script) && o.testOk == null && accentUtils.isEmpty(o.valueJson));
    if (!needsRun) return;
    initialRunRef.current = true;
    onRefreshAll();
    // Mount-only: the seeded artifacts are what decide whether an initial run is due.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Checklist sessions open with the review already underway: the profile's
  // autoFirstPrompt is sent automatically as a 'change' turn so the checklist exists
  // before the user has to ask for anything. Fresh opens with a diff only — resumed
  // transcripts and diff-less opens start idle.
  const chatPromptRef = React.useRef(null);
  const autoPromptRef = React.useRef(false);
  React.useEffect(() => {
    const autoPrompt = safeString(profile.autoFirstPrompt).trim();
    if (!autoPrompt || autoPromptRef.current) return;
    if (Array.isArray(initialSession?.messages) && initialSession.messages.length > 0) return;
    if (!safeString(changeDiffJson).trim()) return;
    autoPromptRef.current = true;
    // Sent through the chat control so the auto-start behaves exactly like the user
    // clicking Send: transcript entry, intent label, and the Send button's built-in
    // spinner/self-disable for the duration of the turn.
    chatPromptRef.current?.send(t(autoPrompt), 'change');
    // Mount-only by design: eligibility is decided once, on open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAddQuery = () => {
    setQueries((prev) => {
      const taken = new Set(prev.map((q) => q.name.toLowerCase()));
      let n = prev.length + 1;
      let name = `query${n}`;
      while (taken.has(name.toLowerCase())) name = `query${++n}`;
      return [...prev, {
        id: makeId(),
        name,
        query: '',
        resultBinding: '',
        resultShape: profile.resultShape,
        splitByColumn: '',
        editorKey: 0,
        lastResult: null,
      }];
    });
  };

  const onRemoveQuery = (id) => {
    setQueries((prev) => prev.filter((q) => q.id !== id));
  };

  const onAddOutput = () => {
    setOutputs((prev) => [...prev, { id: makeId(), name: '', script: '', editorKey: 0, testOk: null, error: '', valueJson: '' }]);
  };

  const onRemoveOutput = (id) => {
    setOutputs((prev) => prev.filter((o) => o.id !== id));
  };

  // The saved Customization this session is bound to (updates in place after the
  // first save-as prompt creates the record).
  const [savedCustomization, setSavedCustomization] = React.useState(() => (
    customization?.id ? { id: customization.id, name: safeString(customization.name) } : null
  ));
  const [saving, setSaving] = React.useState(false);

  /**
   * The persisted definition: everything needed to rehydrate this dialog (and later
   * to run the artifact standalone). Stored as the Customization draft's JSON.
   */
  const buildCustomizationDefinition = React.useCallback(() => JSON.stringify({
    profile: profile.key,
    apiVersion: safeString(apiVersion),
    queries: queries.map((q) => ({
      name: q.name,
      query: safeString(q.query),
      resultBinding: safeString(q.resultBinding),
      resultShape: q.resultShape ?? null,
      splitByColumn: safeString(q.splitByColumn).trim(),
    })),
    outputs: outputs.map((o) => ({ name: o.name, script: safeString(o.script) })),
    templateValues,
    sessionOptions,
  }), [apiVersion, outputs, profile.key, queries, sessionOptions, templateValues]);

  /**
   * Persists the session as a Customization draft (creating the record via the
   * name prompt on first save). Returns the saved { id, name }, or null when the
   * user canceled the name prompt or the save failed (an error message is pushed
   * to the chat in that case).
   */
  const saveCustomizationCore = async () => {
    try {
      setSaving(true);
      const definition = buildCustomizationDefinition();

      if (savedCustomization?.id) {
        const ok = await saveCustomizationDraft(savedCustomization.id, definition);
        if (ok !== true) throw new Error('save failed');
        onCustomizationSaved?.(savedCustomization);
        return savedCustomization;
      }

      const named = await showDialog(<SaveCustomizationDlg />);
      if (named?.canceled) return null;

      const id = await createCustomization({
        name: named.name,
        customizationType: profile.key,
        definition,
        description: named.description || null,
      });
      if (!id) throw new Error('create failed');

      const created = { id, name: named.name };
      setSavedCustomization(created);
      onCustomizationSaved?.(created);
      return created;
    } catch {
      pushMsg('error', t('Saving the customization failed.'));
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Workflow-host cross-save: saves the draft and keeps the session open.
  const doSaveCustomization = async (e) => {
    try {
      const saved = await saveCustomizationCore();
      if (saved) {
        pushMsg('assistant', `${t('Saved draft of')} **${saved.name}**. ${t('Publish it from the Customizations page to make it available.')}`);
      }
    } finally {
      e?.promise?.();
    }
  };

  // Customizations-page Save: saves the draft and closes the dialog. A failed save
  // (or a canceled name prompt) keeps the session open so nothing is lost.
  const doSaveAndClose = async (e) => {
    try {
      const saved = await saveCustomizationCore();
      if (saved) {
        closeWithSession({ canceled: false, customization: saved });
      }
    } finally {
      e?.promise?.();
    }
  };

  // "Send Chat as Bug" (shared flow — see reportAssistantBug.js): collect a
  // description, then email the transcript plus the server's diagnostic capture
  // (correlated by diagnosticSessionID) to the tenant's support address. The outcome
  // lands in the chat, so the user sees it in place.
  const [reportingBug, setReportingBug] = React.useState(false);
  const doReportBug = async (e) => {
    try {
      await promptAndReportAssistantBug({
        diagnosticSessionID: diagnosticSessionIdRef.current,
        hostLabel: safeString(hostLabel) || safeString(profile.dialogTitle) || profile.key,
        chatMessages,
        pushMsg,
        onSending: () => setReportingBug(true),
      });
    } finally {
      setReportingBug(false);
      e?.promise?.();
    }
  };

  // The session object every close path returns: hosts that keep it and pass it back
  // via initialSession let the user close the chat, work in the real screens, and
  // resume the same conversation (same diagnostic id, same transcript).
  const buildSession = React.useCallback(() => ({
    diagnosticSessionID: diagnosticSessionIdRef.current,
    messages: chatMessages,
    templateValues,
    sessionOptions,
    // The review checklist (groups + the user's tick states) survives close/reopen
    // with the transcript; undefined for profiles without the checklist lane.
    ...(profile.hasChecklist && checklistGroups.length > 0
      ? { checklistJson: serializeChecklistGroups(checklistGroups) }
      : {}),
    // The intent collected from intake answers survives too, so a resumed session's
    // requests keep judging against it.
    ...(safeString(collectedIntent).trim() ? { collectedIntent } : {}),
  }), [chatMessages, templateValues, sessionOptions, profile.hasChecklist, checklistGroups, collectedIntent]);

  const closeWithSession = React.useCallback((args) => {
    // The FLAGGED checklist items (state 'unintended') ride every close path so the
    // host can offer to process them — each carries its causing diff path, which is
    // what lets the host navigate to the editor screen that produced the change.
    const flaggedItems = profile.hasChecklist
      ? checklistGroups.flatMap((g) => (Array.isArray(g.items) ? g.items : [])
        .filter((i) => i.state === 'unintended')
        .map((i) => ({
          id: i.id, text: i.text, path: i.path, category: i.category, severity: i.severity, groupTitle: g.title,
        })))
      : [];
    onClose?.({
      ...(args ?? {}),
      session: buildSession(),
      ...(flaggedItems.length > 0 ? { flaggedItems } : {}),
    });
  }, [onClose, buildSession, profile.hasChecklist, checklistGroups]);

  const doOk = (e) => {
    const namedQueries = queries
      .filter((q) => !!safeString(q.query).trim())
      .map((q) => ({ name: q.name, query: safeString(q.query) }));
    closeWithSession({
      canceled: false,
      // Single-query hosts read `query`; multi-query hosts read `queries`.
      ...(hasQueries ? { query: safeString(queries[0]?.query), queries: namedQueries } : {}),
      outputs: outputs
        .filter((o) => !!o.name.trim())
        .map((o) => ({ name: o.name.trim(), script: safeString(o.script) })),
    });
    e?.promise?.();
  };

  // Preview tab: the profile plugin owns how its artifacts are visualised; the default
  // renders generic tables/values for profiles without a custom component.
  const PreviewComponent = profile.previewComponent ?? DefaultQueryShapingPreview;
  const previewQueries = queries.map((q) => ({
    id: q.id,
    name: q.name,
    query: q.query,
    splitByColumn: safeString(q.splitByColumn).trim(),
    lastResult: q.lastResult,
    rows: extractResultRows(q.lastResult?.Result ?? q.lastResult?.result),
  }));

  // Current version seq per artifact name ({ [name]: { queryVersion, mappingVersion } })
  // so a preview (e.g. data export) can stamp generated work with the exact artifact
  // versions it snapshotted. A seq is only reported while the live content still matches
  // that version; an unversioned or mid-edit artifact reports null.
  const getArtifactVersions = React.useCallback(() => {
    const bySheet = {};
    const entryFor = (name) => {
      const key = safeString(name).trim().toLowerCase();
      if (!key) return null;
      bySheet[key] = bySheet[key] ?? { queryVersion: null, mappingVersion: null };
      return bySheet[key];
    };
    for (const q of queries) {
      const entry = entryFor(q.name);
      if (!entry) continue;
      const latest = getVersionList(`query:${q.id}`).slice(-1)[0];
      entry.queryVersion = latest && latest.content === safeString(q.query) ? latest.seq : null;
    }
    for (const o of outputs) {
      const entry = entryFor(o.name);
      if (!entry) continue;
      const latest = getVersionList(`output:${o.id}`).slice(-1)[0];
      entry.mappingVersion = latest && latest.content === safeString(o.script) ? latest.seq : null;
    }
    return bySheet;
  }, [queries, outputs, getVersionList]);

  const tabButtonStyle = (selected) => ({
    border: 'none',
    borderBottom: selected ? '2px solid #6f42c1' : '2px solid transparent',
    background: 'none',
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: selected ? 700 : 500,
    color: selected ? '#6f42c1' : '#495057',
    cursor: 'pointer',
  });

  // User verdict on a checklist item (intended | unintended | back to unconfirmed).
  // Purely client state until the next chat turn round-trips it; the server treats
  // these states as the user's own and never lets the assistant change them.
  const onChecklistItemState = React.useCallback((itemId, nextState) => {
    setChecklistGroups((prev) => prev.map((g) => ({
      ...g,
      items: g.items.map((i) => (i.id === itemId ? { ...i, state: nextState } : i)),
    })));
  }, []);

  // Host preview slot: a single node becomes one unnamed tab; an array of
  // { title, content } becomes the left pane's tab set. When provided it REPLACES the
  // default Preview/Artifacts tabs (the host's screens are the session's real subject).
  // Checklist profiles lead with the Checklist tab (the session's deliverable); the
  // host's tabs (e.g. the Changes diff) follow it.
  const hostPreviewTabs = React.useMemo(() => {
    const hostTabs = previewContent
      ? (Array.isArray(previewContent) ? previewContent : [{ title: '', content: previewContent }])
        .filter((p) => p && p.content)
      : [];
    if (profile.hasChecklist) {
      return [
        {
          title: t('Checklist'),
          content: (
            <ChangeReviewChecklistPanel
              groups={checklistGroups}
              disabled={busy}
              onItemStateChange={onChecklistItemState}
            />
          ),
        },
        ...hostTabs,
      ];
    }
    return hostTabs.length > 0 ? hostTabs : null;
  }, [previewContent, profile.hasChecklist, checklistGroups, busy, onChecklistItemState]);
  const [hostTabIndex, setHostTabIndex] = React.useState(0);
  // Chat-only sessions (no queries, no outputs — the product review/explainer
  // profiles) have no artifacts to show: without host preview content the left pane
  // is hidden entirely and the conversation takes the full dialog.
  const chatOnly = profile.hasQueries === false && !hasOutputs;
  const showLeftPanel = !!hostPreviewTabs || !chatOnly;

  return (
    <AccentDialog
      headerContent={hostLabel
        ? `${hostLabel} — ${t('AI Assistant')}`
        : (profile.dialogTitle
          ? t(profile.dialogTitle)
          : (hasQueries ? t('Query & Outputs AI Assistant') : t('Outputs AI Assistant')))}
      onClose={closeWithSession}
      relativeHeight={(d) => d.height - 120}
      relativeWidth={(d) => d.width - 120}
      // Actions live in the TITLE BAR (left of the X): the chat panel already fills
      // the dialog's foot with its own controls, so a bottom action bar reads as
      // clutter. Closing via the X is the cancel path.
      headerActionsContent={customizationEditor ? [
        <AccentButton
          key="reportBug"
          tagName="reportBug"
          outline
          disabled={reportingBug || chatMessages.length === 0}
          onClick={doReportBug}
          tooltip={t('Email this chat session and its diagnostic log to your support contact.')}
        >
          {reportingBug ? t('Sending...') : t('Send Chat as Bug...')}
        </AccentButton>,
        // Customizations-page editing: Save persists the draft and closes; the
        // dialog's own Cancel discards. No other actions — the artifacts have no
        // host editor to be handed to.
        <AccentButton
          key="save"
          tagName="save"
          disabled={busy || saving}
          onClick={doSaveAndClose}
          tooltip={t('Save the artifacts as this customization\'s draft and close. Publish from the Customizations page to make the changes available.')}
        >
          {saving ? t('Saving...') : t('Save')}
        </AccentButton>,
      ] : [
        <AccentButton
          key="reportBug"
          tagName="reportBug"
          outline
          disabled={reportingBug || chatMessages.length === 0}
          onClick={doReportBug}
          tooltip={t('Email this chat session and its diagnostic log to your support contact.')}
        >
          {reportingBug ? t('Sending...') : t('Send Chat as Bug...')}
        </AccentButton>,
        ...(allowSaveAsCustomization ? [
          <AccentButton
            key="saveCustomization"
            tagName="saveCustomization"
            outline
            disabled={busy || saving}
            onClick={doSaveCustomization}
            tooltip={savedCustomization
              ? t('Save the current artifacts as a new draft of this customization.')
              : t('Save the current artifacts as a reusable customization.')}
          >
            {saving ? t('Saving...') : (savedCustomization ? t('Save draft') : t('Save as customization...'))}
          </AccentButton>,
        ] : []),
        <AccentButton key="ok" tagName="ok" disabled={busy} onClick={doOk}>
          {profile.okLabel
            ? t(profile.okLabel)
            : (!hasOutputs ? t('Use query') : hasQueries ? t('Use query and outputs') : t('Use outputs'))}
        </AccentButton>,
      ]}
    >
      <div style={{ display: 'flex', gap: 16, height: '100%', minHeight: 0, background: '#fff' }}>

        {/* ── Left panel: host preview slot, or the default tabbed artifacts / preview ── */}
        {showLeftPanel && (
        <div style={{ flex: '1 1 0', minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>

          {status === STATUS_GENERATING && (
            <div style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0' }}><AccentSpinner size="sm" /> {t('Thinking...')}</div>
          )}
          {status === STATUS_TESTING && (
            <div style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0' }}><AccentSpinner size="sm" /> {t('Testing...')}</div>
          )}

          {hostPreviewTabs && (
            <>
              {hostPreviewTabs.length > 1 && (
                <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #dee2e6' }}>
                  {hostPreviewTabs.map((tab, index) => (
                    <button
                      key={index}
                      type="button"
                      data-tagname={`hostTab${index}`}
                      style={tabButtonStyle(hostTabIndex === index)}
                      onClick={() => setHostTabIndex(index)}
                    >
                      {safeString(tab.title) || t('Preview')}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                {(hostPreviewTabs[hostTabIndex] ?? hostPreviewTabs[0]).content}
              </div>
            </>
          )}

          {!hostPreviewTabs && (
          <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #dee2e6' }}>
            <button type="button" data-tagname="tabPreview" style={tabButtonStyle(activeTab === 'preview')} onClick={() => setActiveTab('preview')}>
              {t('Preview')}
            </button>
            <button type="button" data-tagname="tabArtifacts" style={tabButtonStyle(activeTab === 'artifacts')} onClick={() => setActiveTab('artifacts')}>
              {t('Artifacts')}
            </button>
          </div>
          )}

          {!hostPreviewTabs && activeTab === 'preview' && (
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <PreviewComponent
                queries={previewQueries}
                outputs={outputs}
                busy={busy}
                apiVersion={apiVersion}
                sessionOptions={sessionOptions}
                templateValues={templateValues}
                onTemplateValuesChange={onTemplateValuesPatch}
                onSessionOptionsChange={(patch) => setSessionOptions((prev) => ({ ...prev, ...patch }))}
                onRefresh={onRefreshAll}
                onDefect={(message) => pushMsg('error', safeString(message))}
                getArtifactVersions={getArtifactVersions}
              />
            </div>
          )}

          {!hostPreviewTabs && activeTab === 'artifacts' && (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 12, overflowY: 'auto' }}>

          {usedTemplates.length > 0 && (
            <div style={sectionStyle}>
              <div style={{ fontWeight: 700, fontSize: 12 }}>{t('Template values')}</div>
              {usedTemplates.map((token) => (
                <AccentTextUnbound
                  key={`${token}:${templateEpoch}`}
                  label={token}
                  disabled={busy}
                  defaultValue={safeString(templateValues?.[token])}
                  onChange={(value) => setTemplateValues((prev) => ({ ...(prev ?? {}), [token]: safeString(value) }))}
                  maxLength={4000}
                />
              ))}
            </div>
          )}

          {/* ── Queries ── */}
          {queries.map((queryEntry) => {
            const queryTestOk = !!(queryEntry.lastResult?.TestOk ?? queryEntry.lastResult?.testOk);
            const queryError = friendlyError(safeString(queryEntry.lastResult?.Error ?? queryEntry.lastResult?.error));
            const queryRowCount = queryEntry.lastResult?.ValueCount ?? queryEntry.lastResult?.valueCount;
            const queryRows = extractResultRows(queryEntry.lastResult?.Result ?? queryEntry.lastResult?.result);
            return (
              <div key={queryEntry.id} style={sectionStyle}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{queryLabel(queryEntry)}</div>
                {allowAddQueries && (
                  <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1.5fr auto', alignItems: 'end' }}>
                    <AccentTextUnbound
                      label={t('Query name (rows available at ctx.queries.<name>)')}
                      disabled={busy}
                      defaultValue={queryEntry.name}
                      onChange={(v) => updateQuery(queryEntry.id, { name: safeString(v).trim() })}
                      maxLength={120}
                    />
                    <div style={{ paddingBottom: 2 }}>
                      <AccentButton outline size="sm" disabled={busy || queries.length <= 1} onClick={() => onRemoveQuery(queryEntry.id)}>
                        {t('Remove')}
                      </AccentButton>
                    </div>
                  </div>
                )}
                <AccentTemplateTextAreaUnbound
                  key={queryEntry.editorKey}
                  label={profile.queryLanguage === 'sql' ? t('SQL query') : t('OData query')}
                  rows={5}
                  max={8000}
                  templateCatalog={queryCatalog}
                  defaultValue={queryEntry.query}
                  onChange={(v) => updateQuery(queryEntry.id, { query: safeString(v) })}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <AccentButton tagName="testQuery" outline size="sm" disabled={busy || accentUtils.isEmpty(queryEntry.query)} onClick={(e) => onTestQueryClick(queryEntry, e)}>
                    {t('Test query')}
                  </AccentButton>
                  {!!queryEntry.lastResult && (
                    <TestOutcomeBadge ok={queryTestOk} error={queryTestOk ? '' : queryError} />
                  )}
                  {queryTestOk && queryRowCount != null && (
                    <span style={{ fontSize: 11, opacity: 0.6 }}>{queryRowCount} {t('rows')}</span>
                  )}
                </div>
                {queryRows.length > 0 && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{t('Result sample')}</summary>
                    <pre style={{ margin: '6px 0 0 0', padding: '6px 8px', borderRadius: 6, border: '1px solid #dee2e6', background: '#f8f9fa', maxHeight: 220, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11 }}>
                      {JSON.stringify(queryRows.slice(0, 5), null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            );
          })}

          {allowAddQueries && (
            <div>
              <AccentButton outline size="sm" disabled={busy} onClick={onAddQuery}>{t('Add query')}</AccentButton>
            </div>
          )}

          {/* ── Outputs (hidden for query-only profiles) ── */}
          {hasOutputs && (
          <div style={sectionStyle}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{outputsLabel || t('Output Variables')}</div>
            <div style={{ fontSize: 11, opacity: 0.7 }}>
              {outputsHint || (!allowAddOutputs
                ? t('A JavaScript expression over the query result. Ask the AI to write or refine it, or edit it here.')
                : hasQueries
                  ? t('Each output is a JavaScript expression that shapes the query result. Ask the AI to add or change outputs, or edit them here.')
                  : t('Each output is a JavaScript expression computed from the trigger context. Ask the AI to add or change outputs, or edit them here.'))}
            </div>

            {outputs.map((output) => (
              <div key={output.id} style={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8, padding: 10, display: 'grid', gap: 8 }}>
                {allowAddOutputs && (
                <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1.5fr auto', alignItems: 'end' }}>
                  <AccentTextUnbound
                    label={t('Variable name')}
                    disabled={busy}
                    defaultValue={output.name}
                    onChange={(v) => updateOutput(output.id, { name: safeString(v).trim() })}
                    maxLength={120}
                  />
                  <div style={{ paddingBottom: 2 }}>
                    <AccentButton outline size="sm" disabled={busy} onClick={() => onRemoveOutput(output.id)}>
                      {t('Remove')}
                    </AccentButton>
                  </div>
                </div>
                )}
                <AccentTemplateTextAreaUnbound
                  key={output.editorKey}
                  label={t('JavaScript expression')}
                  rows={4}
                  max={12000}
                  templateCatalog={jsCatalog}
                  insertMode="snippet"
                  defaultValue={output.script}
                  onChange={(v) => updateOutput(output.id, { script: safeString(v) })}
                  style={{ width: '100%' }}
                />
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <AccentButton outline size="sm" disabled={busy || accentUtils.isEmpty(output.script)} onClick={(e) => onTestOutputClick(output, e)}>
                    {t('Test output')}
                  </AccentButton>
                  <TestOutcomeBadge ok={output.testOk} error={output.testOk ? '' : output.error} />
                </div>
                {output.testOk && !!output.valueJson && (
                  <details>
                    <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>{t('Evaluated value')}</summary>
                    <pre style={{ margin: '6px 0 0 0', padding: '6px 8px', borderRadius: 6, border: '1px solid #dee2e6', background: '#f8f9fa', maxHeight: 180, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11 }}>
                      {output.valueJson}
                    </pre>
                  </details>
                )}
              </div>
            ))}

            {allowAddOutputs && (
              <div>
                <AccentButton outline size="sm" disabled={busy} onClick={onAddOutput}>{t('Add output variable')}</AccentButton>
              </div>
            )}
          </div>
          )}
          </div>
          )}
        </div>
        )}

        {/* ── Right panel: conversation (full width for chat-only sessions without preview) ── */}
        <div style={showLeftPanel
          ? { flex: '0 0 40%', minWidth: 300, borderLeft: '1px solid #dee2e6', paddingLeft: 16, display: 'flex', flexDirection: 'column', minHeight: 0 }
          : { flex: '1 1 0', minWidth: 300, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {!showLeftPanel && status === STATUS_GENERATING && (
            <div style={{ ...statusStyle, background: '#e3f2fd', color: '#1565c0', marginBottom: 8 }}><AccentSpinner size="sm" /> {t('Thinking...')}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{t('Conversation')}</div>
            {lastTurnUsage && (
              <div
                style={{ fontSize: 10, opacity: 0.45, cursor: 'default', whiteSpace: 'nowrap' }}
                title={
                  `${t('Last request')}: ${lastTurnUsage.ai} ${t('AI calls')} \u00b7 ${lastTurnUsage.data} ${t('data API calls')}\n` +
                  `${t('This session')}: ${sessionUsageRef.current.ai} ${t('AI calls')} \u00b7 ${sessionUsageRef.current.data} ${t('data API calls')}\n` +
                  t('Counts every AI model call and data API query the assistant pipeline made (including automatic tests, repairs and probes).')
                }
              >
                {`${lastTurnUsage.ai} ${t('AI')} \u00b7 ${lastTurnUsage.data} ${t('data')}`}
              </div>
            )}
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <AccentPromptHistory
              messages={chatMessages}
              fillHeight
              renderMessageExtra={renderMessageExtra}
            />
          </div>
          <div style={{ borderTop: '1px solid #dee2e6', paddingTop: 10, marginTop: 8 }}>
            <AccentPromptChat
              ref={chatPromptRef}
              disabled={busy}
              messages={chatMessages}
              onMessagesChange={setChatMessages}
              onSendPrompt={onChatPrompt}
              askLabel={t('Ask a Question')}
              sendLabel={profile.changeActionLabel ? t(profile.changeActionLabel) : undefined}
              placeholder={profile.chatPlaceholder
                ? t(profile.chatPlaceholder)
                : (!hasOutputs
                  ? t('Ask for a query or changes to it...')
                  : hasQueries
                    ? t('Ask for a query, outputs, or changes to either...')
                    : t('Ask for output variables or changes to them...'))}
              templateCatalog={promptTemplateCatalog}
            />
          </div>
        </div>
      </div>
    </AccentDialog>
  );
};
